<template>
    <div class="site-width uk-animation-fade single">
        <Seo canonical="/get-together-koeln" title="CancelCancer Get Together Köln" />

        <div class="menu-headline-distance" />

        <div v-if="loading" class="uk-text-center">
            <div uk-spinner />
        </div>

        <!-- Full -->
        <template v-else-if="isFull && !success">
            <div class="uk-card uk-card-alternative uk-padding site-width single uk-text-center">
                <SiteHeadline
                    switch-headline
                    is-main
                    headline="Das Event ist leider ausgebucht."
                    message="Alle 50 Plätze sind vergeben. Wir hoffen, dich beim nächsten Mal dabei zu haben!" />
            </div>
        </template>

        <!-- Success -->
        <template v-else-if="success">
            <div class="uk-card uk-card-alternative uk-padding site-width single uk-text-center">
                <SiteHeadline
                    is-main
                    switch-headline
                    :message="'Danke, ' + form.forename + '!'"
                    headline="Du bist dabei! Wir freuen uns auf dich am 04. Juli in Köln." />
            </div>
        </template>

        <!-- Form -->
        <template v-else>
            <div class="uk-grid uk-grid-large" uk-grid>

                <!-- Left: Info -->
                <div class="uk-width-1-2@m">
                    <div class="eyebrow">Cancel Cancer</div>
                    <h1 class="hero-headline">
                        <span class="precent">Get Together</span><br />
                        <em class="accent">Köln.</em>
                    </h1>
                    <p class="hero-text">
                        Ein Abend mit Freunden, Gleichgesinnten und Menschen, die gemeinsam etwas bewegen wollen.
                        Komm vorbei, lern uns kennen und lass uns gemeinsam auf eine Kindheit ohne Krebs anstoßen.
                    </p>

                    <div class="event-details">
                        <div class="event-detail-row">
                            <span>📅</span>
                            <span>Samstag, 04. Juli 2026</span>
                        </div>
                        <div class="event-detail-row">
                            <span>🕕</span>
                            <span>19:00 Uhr</span>
                        </div>
                        <div class="event-detail-row">
                            <span>📍</span>
                            <span>Cafe Caprista<br /><span>Venloer Str. 380, 50825 Köln-Ehrenfeld</span></span>
                        </div>
                    </div>

                    <div v-if="slotsRemaining !== null" class="slots-info">
                        <span class="uk-badge slots-badge">{{ slotsRemaining }} Plätze verfügbar</span>
                    </div>
                </div>

                <!-- Right: Form -->
                <div class="uk-width-1-2@m">
                    <div class="uk-card uk-card-alternative uk-padding">
                        <h2 class="form-title uk-margin-bottom">Anmeldung</h2>

                        <span id="error-box" />
                        <InfoBox
                            mode="danger"
                            slim
                            medium
                            v-if="formError"
                            class="uk-margin-small-bottom uk-animation-fade uk-margin-medium-bottom">
                            {{ formError }}
                        </InfoBox>

                        <!-- Salutation -->
                        <div class="uk-margin-small-top">
                            <label class="field-label">Anrede</label>
                            <div class="salutation-row">
                                <div
                                    v-for="s in salutations"
                                    :key="s.value"
                                    class="salutation-option"
                                    :class="{ active: form.salutation === s.value }"
                                    @click="form.salutation = s.value">
                                    {{ s.label }}
                                </div>
                            </div>
                        </div>

                        <!-- Name -->
                        <div class="uk-grid uk-grid-small uk-margin-top" uk-grid>
                            <div class="uk-width-1-2">
                                <label class="field-label">Vorname</label>
                                <input v-model="form.forename" class="uk-input field-input" type="text" />
                            </div>
                            <div class="uk-width-1-2">
                                <label class="field-label">Nachname</label>
                                <input v-model="form.surname" class="uk-input field-input" type="text" />
                            </div>
                        </div>

                        <!-- Email -->
                        <div class="uk-margin-top">
                            <label class="field-label">E-Mail Adresse</label>
                            <input
                                v-model="form.email"
                                class="uk-input field-input"
                                :class="{ 'input-error': emailInvalid }"
                                type="email"
                                @blur="validateEmail" />
                            <div v-if="emailInvalid" class="field-error">
                                Bitte eine gültige E-Mail-Adresse eingeben.
                            </div>
                        </div>

                        <!-- Submit -->
                        <div class="uk-margin-medium-top uk-text-center">
                            <Button
                                is-large
                                has-action
                                :action="submit"
                                :disabled="submitting"
                                mode="success"
                                class="uk-width-1-1">
                                {{ submitting ? 'Wird gesendet...' : 'Ich bin dabei →' }}
                            </Button>
                        </div>

                        <div class="uk-margin-small-top uk-text-center consent-text">
                            Mit der Anmeldung akzeptierst du unsere Teilnahmebedingungen und die Datenschutzerklärung.
                        </div>
                    </div>
                </div>
            </div>
        </template>

        <div class="uk-margin-large-top" />
    </div>
</template>

<script>
import Seo from '../../components/Seo.vue';
import SiteHeadline from '@/components/SiteHeadline.vue';
import Button from '../../components/Button.vue';
import InfoBox from '@/components/InfoBox.vue';
import axios from 'axios';
import UIkit from 'uikit';

export default {
    name: 'GetTogetherKoelnSignup',
    components: {Seo, SiteHeadline, Button, InfoBox},
    data() {
        return {
            loading: true,
            isFull: false,
            slotsRemaining: null,
            success: false,
            submitting: false,
            formError: null,
            emailInvalid: false,
            form: {
                salutation: '',
                forename: '',
                surname: '',
                email: '',
            },
            salutations: [
                {value: 'mrs', label: 'Frau'},
                {value: 'mr', label: 'Herr'},
                {value: 'hello', label: 'Keine Angabe'},
            ],
        };
    },
    created() {
        this.loadStatus();
    },
    methods: {
        loadStatus() {
            axios.get(this.getBaseUrl + '/api/get-together-koeln/status')
                .then((res) => {
                    this.isFull = res.data.is_full;
                    this.slotsRemaining = res.data.slots_remaining;
                    this.loading = false;
                })
                .catch(() => {
                    this.loading = false;
                });
        },
        validateEmail() {
            const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            this.emailInvalid = this.form.email.length > 0 && !re.test(this.form.email);
        },
        submit() {
            this.formError = null;

            if (!this.form.salutation) {
                this.formError = 'Bitte wähle eine Anrede.';
                UIkit.scroll('#error-box', {offset: 100}).scrollTo('#error-box');
                return;
            }

            if (!this.form.forename || !this.form.surname || !this.form.email) {
                this.formError = 'Bitte fülle alle Felder aus.';
                UIkit.scroll('#error-box', {offset: 100}).scrollTo('#error-box');
                return;
            }

            const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!re.test(this.form.email)) {
                this.emailInvalid = true;
                this.formError = 'Bitte eine gültige E-Mail-Adresse eingeben.';
                UIkit.scroll('#error-box', {offset: 100}).scrollTo('#error-box');
                return;
            }

            this.submitting = true;

            axios.post(this.getBaseUrl + '/api/get-together-koeln/signup', {
                salutation: this.form.salutation,
                forename: this.form.forename,
                surname: this.form.surname,
                email: this.form.email,
            })
                .then(() => {
                    this.success = true;
                    window.scrollTo({top: 0, behavior: 'smooth'});
                })
                .catch((e) => {
                    if (e.response && e.response.status === 422) {
                        this.isFull = true;
                    } else {
                        this.formError = 'Ein Fehler ist aufgetreten. Bitte versuche es erneut.';
                    }
                })
                .finally(() => {
                    this.submitting = false;
                });
        },
    },
};
</script>

<style lang="scss" scoped>
.eyebrow {
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: #3ddc97;
    margin-bottom: 12px;
}

.hero-headline {
    font-size: 3rem;
    font-weight: 700;
    line-height: 1.1;
    margin: 0 0 20px;
}

.accent {
    color: #3ddc97;
    font-style: italic;
}

.precent {
    color: #fff !important;
}

.hero-text {
    font-size: 17px;
    line-height: 1.7;
    opacity: 0.85;
    margin-bottom: 24px;
}

.event-details {
    display: flex;
    flex-direction: column;
    gap: 10px;
    margin-bottom: 20px;
}

.event-detail-row {
    display: flex;
    gap: 12px;
    font-size: 15px;
    font-weight: 500;
}

.slots-info {
    margin-top: 4px;
}

.slots-badge {
    border: 1px solid #eee !important;
    color: #fff !important;
    background: transparent !important;
    padding: 8px 16px;
    font-size: 14px;
    height: auto;
}

.form-title {
    font-size: 1.5rem;
    font-weight: 600;
    color: #ddd;
}

.salutation-row {
    display: flex;
    gap: 8px;
}

.salutation-option {
    padding: 8px 18px;
    border-radius: 20px;
    border: 1px solid #333;
    background: #1e1e26;
    font-size: 14px;
    cursor: pointer;
    transition: border-color 0.2s, background 0.2s;

    &:hover {
        border-color: #555;
    }

    &.active {
        background: #3ddc97;
        border-color: #3ddc97;
        color: #121217;
        font-weight: 600;
    }
}

.field-label {
    display: block;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    opacity: 0.7;
    margin-bottom: 6px;
}

.field-input {
    background: #1e1e26 !important;
    border: 1px solid #333 !important;
    color: #fff !important;
    border-radius: 6px;
    height: 48px;

    &:focus {
        border-color: #3ddc97 !important;
        outline: none;
    }
}

.input-error {
    border-color: #f44336 !important;
}

.field-error {
    font-size: 12px;
    color: #f44336;
    margin-top: 4px;
}

.consent-text {
    font-size: 13px;
    opacity: 0.5;
}
</style>
